import { Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DropdownService } from 'src/app/services/dropdown.service'; // Ajusta la ruta
import { AddAddress } from 'src/app/models/security/register-request';
import { DropdownOption, DropDownKeywords } from 'src/app/models/shared/dropdown-option';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent, MatDialogActions, MatDialogTitle } from '@angular/material/dialog';
import { ViewModalEnum } from 'src/app/enums/operation';
import { FormErrorsComponent } from 'src/app/components/form-errors/form-errors.component';

// Google Maps type definitions 
declare var google: any;

@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogContent, MatDialogActions, MatDialogTitle, FormErrorsComponent],
  selector: 'app-address-dialog',
  templateUrl: './address-dialog.component.html',
  styles: [`
    .map-container {
      height: 300px;
      width: 100%;
      margin-top: 15px;
    }
    .address-search-container {
      position: relative;
      width: 100%;
    }
  `]
})
export class AddressDialogComponent implements OnInit {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
  @ViewChild('addressInput', { static: false }) addressInput!: ElementRef;
  
  form!: FormGroup;
  countryList: DropdownOption[] = [];
  stateList: DropdownOption[] = [];
  cityList: DropdownOption[] = [];
  districtList: DropdownOption[] = [];
  
  viewModalEnum = ViewModalEnum;
  map: any;
  marker: any;
  autocomplete: any;
  geocoder: any;
  searchValue: string = '';
  
  get isView(): boolean {
    return this.data.mode === ViewModalEnum.View;
  }

  constructor(
    private fb: FormBuilder,
    private dropdownService: DropdownService,
    private ngZone: NgZone,
    public dialogRef: MatDialogRef<AddressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      addresses: AddAddress[],
      address: AddAddress | null,
      index: number | null,
      mode: ViewModalEnum
    }
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadCountries();
    if(this.data.address) {
      this.form.patchValue({
        id: this.data.address.id || 0,
        countryId: this.data.address.countryId,
        countryName: this.data.address.countryName || '',
        stateId: this.data.address.stateId,
        stateName: this.data.address.stateName || '',
        cityId: this.data.address.cityId,
        city: this.data.address.city || '',
        address: this.data.address.address,
        mapAddress: this.data.address.mapAddress || '',
        latitude: this.data.address.latitude || 0,
        longitude: this.data.address.longitude || 0,
        isPrimary: this.data.address.isPrimary || false,
        districtId: this.data.address.districtId || null,
        districtName: this.data.address.districtName || ''
      });

      this.loadStates(this.data.address.countryId, () => {
        if(this.data?.address?.stateId) this.loadCities(this.data.address.stateId,() => {
          if(this.data?.address?.cityId) this.loadDistricts(this.data.address.cityId);
        });
      });
    }

    // Auto-populate address when mapAddress changes if address is empty
    this.form.get('mapAddress')?.valueChanges.subscribe(mapAddress => {
      const address = this.form.get('address')?.value;
      if (!address || address === '') {
        this.form.patchValue({ address: mapAddress });
      }
    });

    this.form.get('cityId')?.valueChanges.subscribe(cityId => {
      if(!cityId) {
        this.form.patchValue({ city: null }, { emitEvent: false });
        return;
      }
      const foundCity = this.cityList.find(ct => ct.id === +cityId);
      this.form.patchValue({ city: foundCity?.text || null }, { emitEvent: false });
    });

    this.form.get('districtId')?.valueChanges.subscribe(districtId => {
      if(!districtId) {
        this.form.patchValue({ districtName: null }, { emitEvent: false });
        return;
      }
      const foundDistrict = this.districtList.find(ct => ct.id === +districtId);
      this.form.patchValue({ districtName: foundDistrict?.text || null }, { emitEvent: false });
    });

    // Disable form if in view mode
    if(this.isView) {
      this.form.disable();
    }
  }

  ngAfterViewInit() {
    // Initialize Google Maps after the view is initialized
    // Allow some time for the view to be properly rendered
    setTimeout(() => {
      this.initGoogleMaps();
    }, 100);
  }

  initGoogleMaps() {
    try {
      // Create a new Google Maps instance
      const mapOptions = {
        zoom: 15,
        center: { lat: 18.4891, lng: -69.8908 }, // Default center (Santo Domingo)
        mapTypeId: google.maps.MapTypeId.ROADMAP
      };
      
      this.map = new google.maps.Map(this.mapContainer.nativeElement, mapOptions);
      this.geocoder = new google.maps.Geocoder();
      
      // Use standard Marker API to avoid compatibility issues
      this.marker = new google.maps.Marker({
        position: mapOptions.center,
        map: this.map,
        draggable: !this.isView
      });

      // If we have latitude and longitude in our form, center the map there
      const lat = this.form.get('latitude')?.value;
      const lng = this.form.get('longitude')?.value;
      
      if (lat && lng) {
        const position = { lat, lng };
        this.map.setCenter(position);
        this.marker.setPosition(position);
      }

      // Setup marker drag event to update form values
      if (!this.isView) {
        google.maps.event.addListener(this.marker, 'dragend', (event: any) => {
          this.ngZone.run(() => {
            const position = this.marker.getPosition();
            this.updateFormLocation(position.lat(), position.lng());
            this.reverseGeocode(position.lat(), position.lng());
          });
        });

        // Setup map click event to move marker
        google.maps.event.addListener(this.map, 'click', (event: any) => {
          this.ngZone.run(() => {
            this.marker.setPosition(event.latLng);
            const position = this.marker.getPosition();
            this.updateFormLocation(position.lat(), position.lng());
            this.reverseGeocode(position.lat(), position.lng());
          });
        });

        // Setup autocomplete for address input with language set to Spanish
        this.autocomplete = new google.maps.places.Autocomplete(this.addressInput.nativeElement, {
          language: 'es' // Configurar el idioma a español
        });
        this.autocomplete.addListener('place_changed', () => {
          this.ngZone.run(() => {
            const place = this.autocomplete.getPlace();
            if (!place.geometry) {
              return;
            }
            
            // Update map and marker position
            this.map.setCenter(place.geometry.location);
            this.marker.setPosition(place.geometry.location);
            
            // Update form location
            const position = this.marker.getPosition();
            this.updateFormLocation(position.lat(), position.lng());
            
            // Extract and set address fields
            this.searchValue = this.addressInput.nativeElement.value;
            this.processPlaceDetails(place);
          });
        });
      }
    } catch (error) {
      console.error('Error initializing Google Maps:', error);
    }
  }

  processPlaceDetails(place: any) {
    // Extract address components
    let country = '';
    let countryCode = '';
    let state = '';
    let city = '';
    let streetAddress = '';
    
    for (const component of place.address_components) {
      const componentType = component.types[0];
      
      switch (componentType) {
        case 'country':
          country = component.long_name;
          countryCode = component.short_name; // Add ISO country code
          break;
        case 'administrative_area_level_1':
          state = component.long_name;
          break;
        case 'locality':
        case 'administrative_area_level_2':
          city = component.long_name;
          break;
        case 'route':
          streetAddress += component.long_name + ' ';
          break;
        case 'street_number':
          streetAddress = component.long_name + ' ' + streetAddress;
          break;
      }
    }
    
    // Set Google Maps suggested address in mapAddress field
    this.form.patchValue({ mapAddress: place.formatted_address });
    
    // Find corresponding country, state, and city IDs
    this.findAndSetCountryStateCity(country, countryCode, state, city);
  }

  findAndSetCountryStateCity(country: string, countryCode: string, state: string, city: string) {
    // Try to find country by ISO code first, then fallback to name matching
    let foundCountry = this.countryList.find(c => 
      c.keywords === countryCode || // Add a keywords field to store ISO codes in your dropdown options
      c.text.toLowerCase() === country.toLowerCase());
    
    if (foundCountry) {
      this.form.patchValue({ countryId: foundCountry.id });
      this.form.patchValue({ countryName: foundCountry.text });
      
      // Load states for this country and then find matching state
      this.loadStates(foundCountry.id, () => {
        // Try fuzzy matching for state names
        let foundState = this.findBestMatch(state, this.stateList);
        
        if (foundState) {
          this.form.patchValue({ stateId: foundState.id });
          this.form.patchValue({ stateName: foundState.text });
          
          // Load cities for this state and then find matching city
          this.loadCities(foundState.id, () => {
            // Try fuzzy matching for city names
            let foundCity = this.findBestMatch(city, this.cityList);
            
            if (foundCity) {
              this.form.patchValue({ cityId: foundCity.id });
              this.form.patchValue({ city: foundCity.text });
            }
          });
        }
      });
    }
  }

  // Helper function to find the best match using fuzzy matching
  findBestMatch(searchText: string, options: DropdownOption[]): DropdownOption | undefined {
    if (!searchText || options.length === 0) return undefined;
    
    // First try exact match
    let match = options.find(option => 
      option.text.toLowerCase() === searchText.toLowerCase());
    
    if (match) return match;
    
    // Then try includes
    match = options.find(option => 
      option.text.toLowerCase().includes(searchText.toLowerCase()) || 
      searchText.toLowerCase().includes(option.text.toLowerCase()));
    
    if (match) return match;
    
    // If no match found, try to use a predefined mapping for common values
    // You could expand this with more sophisticated fuzzy matching if needed
    const knownMappings: Record<string, string[]> = {
      // Países (English to Spanish)
      'United States': ['Estados Unidos', 'USA', 'EE.UU.'],
      'Spain': ['España'],
      'United Kingdom': ['Reino Unido', 'Inglaterra', 'Gran Bretaña'],
      'Mexico': ['México'],
      'Canada': ['Canadá'],
      'Brazil': ['Brasil'],
      'Argentina': ['Argentina'],
      'France': ['Francia'],
      'Germany': ['Alemania'],
      'Italy': ['Italia'],
      'Portugal': ['Portugal'],
      'Netherlands': ['Países Bajos', 'Holanda'],
      'China': ['China'],
      'Japan': ['Japón'],
      'Dominican Republic': ['República Dominicana'],
      'Colombia': ['Colombia'],
      'Peru': ['Perú'],
      'Chile': ['Chile'],
      'Ecuador': ['Ecuador'],
      'Venezuela': ['Venezuela'],
      'Costa Rica': ['Costa Rica'],
      'Panama': ['Panamá'],

      // Estados/Provincias (English to Spanish)
      'Barcelona Province': ['Barcelona'],
      'Madrid Province': ['Madrid'],
      'Catalonia': ['Cataluña'],
      'Andalusia': ['Andalucía'],
      'Valencia Region': ['Valencia', 'Comunidad Valenciana'],
      'New York State': ['Nueva York Estado'],
      'Florida State': ['Florida Estado'],
      'California State': ['California Estado'],
      'Texas': ['Texas', 'Tejas'],
      'Illinois': ['Illinois'],
      'Quebec': ['Quebec', 'Québec'],
      'Ontario': ['Ontario'],
      'Paris Region': ['Región de París'],
      'Roma Region': ['Región de Roma'],
      'Lisbon District': ['Distrito de Lisboa'],
      'Tokyo Prefecture': ['Prefectura de Tokio'],
      'Beijing Province': ['Provincia de Pekín'],
      'Distrito Nacional DR': ['National District'],
      'Santo Domingo Province': ['Provincia Santo Domingo'],
      'Santiago Province': ['Provincia Santiago'],

      // Ciudades (English to Spanish)
      'New York City': ['Nueva York Ciudad', 'Ciudad de Nueva York'],
      'Los Angeles': ['Los Ángeles'],
      'Miami City': ['Miami Ciudad'],
      'Chicago': ['Chicago'],
      'Houston': ['Houston'],
      'Madrid City': ['Madrid Ciudad'],
      'Barcelona City': ['Barcelona Ciudad'],
      'London': ['Londres'],
      'Paris City': ['París Ciudad'],
      'Rome': ['Roma'],
      'Berlin': ['Berlín'],
      'Amsterdam': ['Ámsterdam'],
      'Tokyo City': ['Tokio Ciudad'],
      'Mexico City': ['Ciudad de México', 'México DF', 'CDMX'],
      'Rio de Janeiro': ['Río de Janeiro'],
      'Sao Paulo': ['São Paulo'],
      'Buenos Aires': ['Buenos Aires'],
      'Bogota': ['Bogotá'],
      'Santiago City': ['Santiago Ciudad'],
      'Lima': ['Lima'],
      'Caracas': ['Caracas'],
      'Santo Domingo City': ['Santo Domingo Ciudad']
    };
    
    // Check if we have a known mapping
    for (const option of options) {
      const optionName = option.text.toLowerCase();
      for (const [key, values] of Object.entries(knownMappings)) {
        if (searchText.toLowerCase().includes(key.toLowerCase()) || 
            key.toLowerCase().includes(searchText.toLowerCase())) {
          if (values.some(v => optionName.includes(v.toLowerCase()) || v.toLowerCase().includes(optionName))) {
            return option;
          }
        }
      }
    }
    
    // If everything fails, return the first option or undefined
    return undefined;
  }

  updateFormLocation(lat: number, lng: number) {
    this.form.patchValue({
      latitude: lat,
      longitude: lng
    });
  }

  reverseGeocode(lat: number, lng: number) {
    this.geocoder.geocode({ 
      location: { lat, lng },
      language: 'es' // Configurar el idioma a español
    }, (results: any, status: any) => {
      if (status === 'OK' && results[0]) {
        this.ngZone.run(() => {
          // Set Google Maps suggested address in mapAddress field
          this.form.patchValue({ mapAddress: results[0].formatted_address });
          
          // Extract country, state, city info
          let country = '';
          let countryCode = '';
          let state = '';
          let city = '';
          
          for (const component of results[0].address_components) {
            const componentType = component.types[0];
            
            switch (componentType) {
              case 'country':
                country = component.long_name;
                countryCode = component.short_name;
                break;
              case 'administrative_area_level_1':
                state = component.long_name;
                break;
              case 'locality':
              case 'administrative_area_level_2':
                city = component.long_name;
                break;
            }
          }
          
          // Find corresponding country, state, and city IDs
          this.findAndSetCountryStateCity(country, countryCode, state, city);
        });
      }
    });
  }

  buildForm() {
    this.form = this.fb.group({
      id: [0],
      countryId: ['', Validators.required],
      countryName: [''],
      stateId: ['', Validators.required],
      stateName: [''],
      cityId: ['', Validators.required],
      city: [''],
      address: ['', Validators.required],
      mapAddress: [''],
      latitude: [0],
      longitude: [0],
      isPrimary: [false],
      districtId: [''],
      districtName: ['']
    });
  }

  loadCountries() {
    this.dropdownService.GetOptions(DropDownKeywords.Countries).subscribe({
      next: (res) => {
        this.countryList = res;
      }
    });
  }

  onChangeCountry() {
    const countryId = this.form.value.countryId;
    this.form.patchValue({ stateId: null, cityId: null, countryName: null }); // Reiniciamos y limpiamos countryName
    this.stateList = [];
    this.cityList = [];
    this.districtList = [];
    if(countryId) {
      this.loadStates(countryId, () => {
        const found = this.countryList.find(c => c.id === +countryId);
        this.form.patchValue({ countryName: found?.text || null });
      });
    }
  }

  loadStates(countryId: number, callback?: () => void) {
    const typeId = 1; // Provinces by countryId
    this.dropdownService.GetOptions(DropDownKeywords.Provinces,  typeId, `${countryId}`).subscribe({
      next: (res) => {
        this.stateList = res;
        callback?.();
      }
    });
  }

  onChangeState() {
    const stateId = this.form.value.stateId;
    this.form.patchValue({ cityId: null, stateName: null }); // Limpieza de stateName
    this.cityList = [];
    this.districtList = [];
    if(stateId) {
      this.loadCities(stateId, () => {
        const found = this.stateList.find(s => s.id === +stateId);
        this.form.patchValue({ stateName: found?.text || null });
      });
    }
  }

  loadCities(stateId: number, callback?: () => void) {
    const typeId = 1; // Provinces by stateId
    this.dropdownService.GetOptions(DropDownKeywords.Cities, typeId, `${stateId}`).subscribe({
      next: (res) => {
        this.cityList = res;
        callback?.();
      }
    });
  }

  onChangeCities() {
    const cityId = this.form.value.cityId;
    this.form.patchValue({ districtId: null, cityName: null }); // Limpieza de cityName
    this.districtList = [];
    if(cityId) {
      this.loadDistricts(cityId, () => {
        const found = this.districtList.find(s => s.id === +cityId);
        this.form.patchValue({ cityName: found?.text || null });
      });
    }
  }

  loadDistricts(cityId: number, callback?: () => void) {
    const typeId = 1; // Districts by cityId
    this.dropdownService.GetOptions(DropDownKeywords.Districts, typeId, `${cityId}`).subscribe({
      next: (res) => {
        this.districtList = res;
        callback?.();
      }    
    });
  }


  // Update map location when address is entered manually
  searchAddress() {
    const searchValue = this.addressInput.nativeElement.value;
    if (searchValue) {
      this.geocoder.geocode({ 
        address: searchValue,
        language: 'es' // Configurar el idioma a español
      }, (results: any, status: any) => {
        if (status === 'OK' && results[0]) {
          this.ngZone.run(() => {
            const location = results[0].geometry.location;
            this.map.setCenter(location);
            this.marker.setPosition(location);
            
            const position = this.marker.getPosition();
            this.updateFormLocation(position.lat(), position.lng());
            
            // Set Google Maps suggested address in mapAddress field
            this.form.patchValue({ mapAddress: results[0].formatted_address });
            
            // Process address components for dropdown selection
            this.searchValue = searchValue;
            this.processPlaceDetails(results[0]);
          });
        }
      });
    }
  }

  closeDialog() {
    this.dialogRef.close(null);
  }

  save() {
    if(this.form.invalid) 
     {
      this.form.markAllAsTouched();
      return;
     }      
    const formValue = this.form.value;
    
    // Create the address object
    const val: AddAddress = {
      id: formValue.id,
      countryId: formValue.countryId || null,
      countryName: formValue.countryName,
      stateId: formValue.stateId || null,
      stateName: formValue.stateName,
      cityId: formValue.cityId || null,
      city: formValue.city,
      districtId: formValue.districtId || null,
      districtName: formValue.districtName,
      // Usar la dirección personalizada para el campo address
      address: formValue.address,
      // La dirección de Google va en mapAddress
      mapAddress: formValue.mapAddress,
      latitude: formValue.latitude,
      longitude: formValue.longitude,
      isPrimary: formValue.isPrimary
    };
    
    if(val.isPrimary) {
      this.data.addresses.forEach(a => a.isPrimary = false);
    }
    
    if(this.data.mode === ViewModalEnum.Create) {
      this.data.addresses.push({...val});
    } else if(this.data.mode === ViewModalEnum.Edit && this.data.index != null) {
      this.data.addresses[this.data.index] = {...val};
    }
    
    this.dialogRef.close(this.data.addresses);
  }
}
